import { useLocation } from '@tanstack/react-router';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { type UseStaleBundleReloadOptions, useStaleBundleReload } from 'src/hooks/useStaleBundleReload';
import { IS_DEPLOYED_BUILD, type StaleBundleMonitor } from 'src/lib/buildVersion';
import { getAppStaleBundleMonitor } from 'src/lib/buildVersion/appStaleBundleMonitor';
import { useComposerWorkAtRisk } from 'src/modules/ai';
import { useEditingState, useTransitionCallback } from 'src/providers/EditingStateProvider';
import { useHasPageWorkAtRisk, usePageWorkAtRiskCheck } from 'src/providers/PageWorkAtRiskProvider';

type Props = {
  /** Injection seam for tests. The app always uses the singleton. */
  monitor?: StaleBundleMonitor;
  enabled?: boolean;
};

/** Warehouse tablets: 44px minimum tap target. AgButton's `md` is 40px. */
const TAP_TARGET = 'min-h-[44px]';

const flushOnly = async () => {};

/**
 * Renders nothing until this tab's bundle goes stale, then either swaps onto
 * the deployed one (at a navigation, with nothing outstanding) or offers the
 * swap as a toast.
 */
export default function StaleBundleNotice({
  monitor = getAppStaleBundleMonitor(),
  enabled = IS_DEPLOYED_BUILD,
}: Props) {
  const isEditingActive = useEditingState((state) => state.getIsActive());
  const isComposerWorkAtRisk = useComposerWorkAtRisk();
  const isPageWorkAtRisk = useHasPageWorkAtRisk();
  const getPageWorkAtRisk = usePageWorkAtRiskCheck();
  const getHasUnsavedWork = useCallback(
    () => isEditingActive || isComposerWorkAtRisk || Boolean(getPageWorkAtRisk?.()),
    [isEditingActive, isComposerWorkAtRisk, getPageWorkAtRisk]
  );
  const routeKey = useLocation({ select: (location) => location.pathname });

  // `useTransitionCallback` commits open grid rows and waits for in-flight
  // saves to settle (behind the app's "Waiting for changes to save..."), but
  // it also marks the tab busy for as long as it runs — and that same flag is
  // `NavigationBlocker`'s `beforeunload` guard. So the unload is a SECOND
  // step, taken from an effect once the flag is down.
  const [isSwapping, setIsSwapping] = useState(false);
  const flushEdits = useTransitionCallback(flushOnly);
  const reload = useCallback(() => {
    void flushEdits().then(() => setIsSwapping(true));
  }, [flushEdits]);

  useEffect(() => {
    if (isSwapping) window.location.reload();
  }, [isSwapping]);

  const onPrompt = useCallback<UseStaleBundleReloadOptions['onPrompt']>((_status, acceptReload) => {
    enqueueSnackbar('Agriful has been updated. Reload to keep everything working.', {
      action: (key) => (
        <>
          <AgButton className={TAP_TARGET} size="md" variant="ghost-inverted" onPress={acceptReload}>
            Reload
          </AgButton>
          <AgButton className={TAP_TARGET} size="md" variant="ghost-inverted" onPress={() => closeSnackbar(key)}>
            Later
          </AgButton>
        </>
      ),
      persist: true,
      variant: 'info',
    });
  }, []);

  useStaleBundleReload({
    enabled,
    hasUnsavedWork: isEditingActive || isComposerWorkAtRisk || isPageWorkAtRisk,
    getHasUnsavedWork: getPageWorkAtRisk ? getHasUnsavedWork : undefined,
    monitor,
    onPrompt,
    reload,
    routeKey,
  });

  return null;
}
